import type { VertexModelUsage } from "@/features/llm-usage/api/llm-usage.api";

export interface VertexRegionUsage {
  location: string;
  totalTokens: number;
}

export interface VertexModelRollup {
  model: string;
  publisher: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  /** Every serving region that contributed, ranked by its own total. */
  regions: VertexRegionUsage[];
}

/**
 * Collapses Cloud Monitoring's per-region series into one row per model.
 *
 * Monitoring keys the metric by serving region, which is where Vertex happened
 * to run the call — not a dimension an operator can act on. Left split, one
 * model spread over fourteen regions reads as fourteen small rows while a
 * `global` model reads as one large one, so ranking them together compares a
 * whole against fragments and the busiest model never appears at the top.
 */
export function rollUpVertexModels(rows: readonly VertexModelUsage[]): VertexModelRollup[] {
  const byModel = new Map<string, VertexModelRollup>();

  for (const row of rows) {
    const key = `${row.publisher}\u0000${row.model}`;
    const rollup = byModel.get(key) ?? {
      model: row.model,
      publisher: row.publisher,
      inputTokens: 0,
      outputTokens: 0,
      totalTokens: 0,
      regions: [],
    };

    rollup.inputTokens += row.inputTokens;
    rollup.outputTokens += row.outputTokens;
    rollup.totalTokens += row.totalTokens;
    rollup.regions.push({ location: row.location, totalTokens: row.totalTokens });

    byModel.set(key, rollup);
  }

  for (const rollup of byModel.values()) {
    rollup.regions.sort((a, b) => b.totalTokens - a.totalTokens);
  }

  return [...byModel.values()].sort((a, b) => b.totalTokens - a.totalTokens);
}
